import { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { FileText, Copy, Check, RefreshCw } from 'lucide-react';
import { Button } from '@/client/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/client/components/ui/dialog';
import { API_BASE_URL } from '@/client/config';
import { useEncoderWSSStore } from '@/client/store/encoderWSSStore';

interface ExportModalProps {
  encoderId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ExportModal({ encoderId, open, onOpenChange }: ExportModalProps) {
  const { getAccessTokenSilently } = useAuth0();
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Get WSS events for this encoder from the client-side store
  const getEncoderEvents = () => {
    if (!encoderId) return [];
    const encoder = useEncoderWSSStore.getState().encoders.get(encoderId);
    return encoder?.events || [];
  };

  const fetchExport = async (id: string) => {
    try {
      setLoading(true);
      setError(null);
      setCopied(false);

      const token = await getAccessTokenSilently();
      const wssEvents = getEncoderEvents();

      const response = await fetch(`${API_BASE_URL}/api/triage/${id}/export`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ wssEvents }),
      });

      if (!response.ok) throw new Error('Failed to export triage data');

      const data = await response.json();
      setContent(data.text);
    } catch (err) {
      console.error('Failed to export triage data:', err);
      setError('Failed to export triage data');
      setContent('');
    } finally {
      setLoading(false);
    }
  };

  // Fetch when modal opens with an encoder
  useEffect(() => {
    if (open && encoderId) {
      fetchExport(encoderId);
    }
    if (!open) {
      // Clear state when closing
      setContent('');
      setError(null);
      setCopied(false);
    }
  }, [open, encoderId]);

  const handleCopyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Encoder Health Report
          </DialogTitle>
          <DialogDescription className="font-mono">
            {encoderId && `Encoder ID: ${encoderId}`}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
              <span className="ml-2 text-muted-foreground">Loading export data...</span>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center py-12 text-red-500">
              {error}
            </div>
          ) : (
            <pre className="bg-muted p-4 rounded-md text-xs font-mono whitespace-pre overflow-auto max-h-[50vh]">
              {content}
            </pre>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button
            variant="outline"
            onClick={handleCopyToClipboard}
            disabled={loading || !content}
          >
            {copied ? (
              <>
                <Check className="h-4 w-4 mr-2 text-green-600" />
                Copied!
              </>
            ) : (
              <>
                <Copy className="h-4 w-4 mr-2" />
                Copy to Clipboard
              </>
            )}
          </Button>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
